import { useEffect, useState } from 'react';

/**
 * Custom hook to dynamically calculate and set navbar height
 * Updates CSS custom property --navbar-height based on actual navbar element height
 * This hook should be used in the main App component to work globally
 */
export const useNavbarHeight = () => {
  const [navbarHeight, setNavbarHeight] = useState(66.5); // Default fallback height

  useEffect(() => {
    const updateNavbarHeight = () => {
      const navbar = document.querySelector('.navbar');
      if (navbar) {
        const height = navbar.offsetHeight;
        setNavbarHeight(height);
        // Set CSS custom property for dynamic padding
        document.documentElement.style.setProperty('--navbar-height', `${height}px`);
      }
    };

    // Initial calculation with a small delay to ensure navbar is rendered
    const timeoutId = setTimeout(updateNavbarHeight, 100);

    // Create ResizeObserver to watch for navbar height changes
    const resizeObserver = new ResizeObserver(updateNavbarHeight);

    // Use MutationObserver to watch for navbar changes (e.g., when it's added/removed)
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          const navbar = document.querySelector('.navbar');
          if (navbar) {
            resizeObserver.observe(navbar);
            updateNavbarHeight();
          }
        }
      });
    });

    // Start observing the document body for navbar changes
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also listen for window resize events as a fallback
    window.addEventListener('resize', updateNavbarHeight);

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', updateNavbarHeight);
    };
  }, []);

  return navbarHeight;
};
